import {
    ChangeDetectionStrategy,
    Component,
    HostBinding,
    Injector,
    input,
    Input,
    OnInit,
    ViewEncapsulation,
    computed,
    inject,
} from '@angular/core';
import { FiRemoteStateFullDataSource } from '../../../fi-items-list/fi-remote-state-full-data-source';
import { IChecklist2ScheduleParameter, IChecklistScheduleForList } from '../../checklist-v2';
import { IItemsListAction2, ISideListOptionsItem } from '../../../fi-items-list/fi-items-list';
import { ObjectTypes, SideListOptionType } from '../../../types';
import { ILoadDataArgs } from '../../../fi-items-list/fi-data-source-base.directive';
import { FiItemsListType } from '../../../fi-items-list/fi-items-list-type';
import { FiChecklist2ScheduleService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule.service';
import { FiChecklist2Service } from '@fi-shared/checklist/fi-checklist2.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { FiChecklist2ScheduleDialogService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-dialog.service';
import { FiChecklist2ScheduleFormatService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-format.service';
import { FiChecklistScheduleType } from '@fi-shared/checklist/schedule/schedule-edit/fi-checklist-schedule-type';
import { FiItemsListOptionType } from '@fi-shared/fi-items-list/fi-items-list-option-type';
import { FiDialogConfirmDeleteComponent } from '@fi-shared/fi-dialogs-generic/confirm-delete/dialog-confirm-delete.component';
import { FiChecklistHelperService } from '@fi-shared/checklist/fi-checklist-helper.service';
import { PublicationItemStatus } from '@fi-shared/fi-edit-publication/publication-item-status';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';
import { IItemsListDataFetchResult } from '@fi-shared/fi-items-list/fi-items-list';

@Component({
    selector: 'fi-checklist2-schedule-list',
    templateUrl: './schedule-list.component.html',
    styleUrls: ['./schedule-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-checklist2-schedule-list' },
    standalone: false,
})
export class FiChecklist2ScheduleListComponent
    extends FiRemoteStateFullDataSource<IChecklistScheduleForList>
    implements OnInit
{
    @Input() checklistScheduleIds: number[];
    public auditMode = input.required<boolean>();

    protected sideListOptions: ISideListOptionsItem[];
    public actions: IItemsListAction2[] = [];

    public readonly defaultSortColumnName = 'DueDateTime';
    public readonly defaultSortDirection = false;
    public readonly objectType = ObjectTypes.ChecklistSchedule;

    public readonly itemsListType = FiItemsListType.ChecklistSchedules;

    private checklistV2RootState = computed(() => FiStateNames.getChecklistV2RootState(this.auditMode()));

    protected getRowClassFn = (item: IChecklistScheduleForList): string => {
        return !this.isChecklistScheduled(item) || this.canEditSchedule(item) ? 'cursor-pointer' : '';
    };

    @HostBinding('class.mobile-view') get isMobileView(): boolean {
        return this.detectDeviceService.isMobilePhone();
    }

    private fiChecklist2Service = inject(FiChecklist2Service);
    private fiChecklist2ScheduleService = inject(FiChecklist2ScheduleService);
    private fiChecklistHelperService = inject(FiChecklistHelperService);
    private detectDeviceService = inject(DetectDevicesService);
    private fiItemsDeleteHelperService = inject(FiItemsDeleteHelperService);
    private fiChecklist2ScheduleDialogService = inject(FiChecklist2ScheduleDialogService);
    private fiChecklist2ScheduleFormatService = inject(FiChecklist2ScheduleFormatService);

    constructor(injector: Injector) {
        super(injector)
    }

    public ngOnInit(): void {
        this.initSideList();
        this.actions = this.getChecklistScheduleListActions();
    }

    private initSideList(): void {
        this.sideListOptions = [
            { value: SideListOptionType.SharedWithMe, text: this.appTextService.get('common.shared_with_me') },
        ];

        // "My checklists" is not allowed for "read" users
        if (this.fiChecklist2Service.isChecklistShareRights(this.auditMode())) {
            this.sideListOptions.push({
                value: SideListOptionType.CreatedBy,
                text: this.appTextService.get('common.created_by_me'),
            });
        }

        if (!this.auditMode()) {
            this.sideListOptions.push(
                {
                    value: SideListOptionType.RecurringCheckLists,
                    text: this.appTextService.get('checklist.recurring_schedules'),
                },
                {
                    value: SideListOptionType.OneTimeCheckLists,
                    text: this.appTextService.get('checklist.one_time_schedules'),
                },
                {
                    value: SideListOptionType.OnDemandChecklists,
                    text: this.appTextService.get('checklist.on_demand_schedules'),
                },
            );
        }
    }

    private getChecklistScheduleListActions(): IItemsListAction2<IChecklistScheduleForList>[] {
        return [
            {
                title: `${this.appTextService.get('checklist.edit_schedule')}`,
                condition: (data) => {
                    return this.canEditSchedule(data);
                },
                onClick: (data) => {
                    this.fiStateService.go(this.checklistV2RootState().Schedule.Edit, { id: data.Id });
                },
                iconClass: 'far fa-pen-to-square',
                optionType: FiItemsListOptionType.Icon,
            },
            {
                title: this.appTextService.get('checklist.delete_schedule'),
                condition: (data) => {
                    return this.canEditSchedule(data);
                },
                onClick: (data) => {
                    this.openDeleteConfirmationDialog(data);
                },
                iconClass: 'far fa-trash',
                optionType: FiItemsListOptionType.Icon,
            },
        ];
    }

    private canEditSchedule(scheduleListItem: IChecklistScheduleForList): boolean {
        return (
            this.fiChecklist2Service.canEditScheduleBySharingSelection(scheduleListItem, this.auditMode()) ||
            scheduleListItem.CreatedByUserId === this.userDataService.getUserId()
        );
    }

    private openDeleteConfirmationDialog(scheduleListItem: IChecklistScheduleForList): void {
        if (scheduleListItem.HasAnswers) {
            this.fiDialogsService.custom<void, boolean>({
                headerText: this.appTextService.get('checklist.schedule_delete_dialog_header'),
                messageText: this.appTextService.get('checklist.delete_schedule_with_answers_not_allowed'),
                colorClass: 'red',
                isTopCloseButtonShown: true,
                headerIconClass: 'fa-light fa-trash red',
                isYesButtonHidden: true,
                noButtonText: this.appTextService.get('ok'),
                preventUppercaseButtonLabel: true,
                config: {
                    maxWidth: '640px',
                    autoFocus: false,
                    panelClass: 'fi-rounded-dialog-panel',
                },
            });
            return;
        }
        this.fiDialogsService
            .custom({
                headerText: this.appTextService.get('checklist.schedule_delete_dialog_header'),
                headerIconClass: 'fa-regular fa-trash red',
                colorClass: 'red',
                isTopCloseButtonShown: true,
                preventUppercaseButtonLabel: true,
                contentComponentRef: FiDialogConfirmDeleteComponent,
                contentComponentData: {
                    step1: {
                        message: 'checklist.schedule_delete_dialog_confirm_text',
                        buttonText: this.appTextService.get('common.confirm'),
                        confirmTextToInput: this.appTextService.get('common.delete').toUpperCase(),
                        preventUppercaseButtonLabel: true,
                    },
                },
                config: {
                    maxWidth: '600px',
                    panelClass: 'fi-rounded-dialog-panel',
                },
            })
            .onYesButtonClick(() => this.deleteChecklistSchedule(scheduleListItem));
    }

    private deleteChecklistSchedule(scheduleListItem: IChecklistScheduleForList): void {
        this.fiChecklist2ScheduleService.deleteChecklistSchedules([scheduleListItem.Id], this.auditMode()).then(() => {
            const objectType = !this.auditMode ? ObjectTypes.ChecklistSchedule : ObjectTypes.Checklist2AuditSchedule;
            this.fiItemsDeleteHelperService.showDeletedObjectSimpleSnackBar(
                objectType,
                [scheduleListItem.Id],
                scheduleListItem.Name,
            );

            this.removeData([scheduleListItem]); // for excluding deleted checklist schedule from list
        });
    }

    public loadListDataFn(loadArgs: ILoadDataArgs): Promise<IItemsListDataFetchResult<IChecklistScheduleForList>> {
        const parameter = this.getItemsListParameter(loadArgs) as IChecklist2ScheduleParameter;
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: !loadArgs.fetchOnlyItems,
        };
        if (this.checklistScheduleIds) {
            parameter.Ids = this.checklistScheduleIds;
        }
        parameter.AuditMode = this.auditMode();
        return this.fiChecklist2ScheduleService.getChecklistSchedulesListData(parameter);
    }

    protected getScheduleTypeName(scheduleType: FiChecklistScheduleType): string {
        return this.fiChecklist2ScheduleFormatService.getScheduleTypeText(scheduleType, true);
    }

    protected getScheduleName(item: IChecklistScheduleForList): string {
        return this.isChecklistScheduled(item) ? item.Name : this.appTextService.get('checklist.not_scheduled');
    }

    protected isChecklistScheduled(item: IChecklistScheduleForList): boolean {
        return !!item.Id;
    }

    protected onRowClick(item: IChecklistScheduleForList): void {
        if (this.isChecklistScheduled(item)) {
            if (this.canEditSchedule(item)) {
                this.fiStateService.go(this.checklistV2RootState().Schedule.Edit, { id: item.Id });
            }
        } else {
            this.fiChecklist2ScheduleDialogService.openAddChecklistScheduleDialogWithChecklistId(
                item.ChecklistId,
                this.auditMode(),
            );
        }
    }

    protected getDueDateTimeText(item: IChecklistScheduleForList): string {
        return this.fiChecklistHelperService.getDueDateTimeText(item, this.isChecklistScheduled(item), true);
    }

    protected getStartTimeText(item: IChecklistScheduleForList): string {
        return this.fiChecklistHelperService.getStartTimeText(item, item.IsActive, item.ScheduleType, true);
    }

    private isOnDemandSchedule(scheduleType: FiChecklistScheduleType): boolean {
        return this.fiChecklistHelperService.isOnDemandSchedule(scheduleType);
    }

    private isAuditSchedule(scheduleType: FiChecklistScheduleType): boolean {
        return this.fiChecklistHelperService.isAuditSchedule(scheduleType);
    }

    protected isStartTimeRed(item: IChecklistScheduleForList): boolean {
        return item.OperationalPeriodStatus === PublicationItemStatus.NotShared;

        /*if (item.IsActive && (this.isOnDemandSchedule(item.ScheduleType) || this.isAuditSchedule(item.ScheduleType)))
            return this.isDueDateSoon(item);*/
    }

    protected isEndTimeRed(item: IChecklistScheduleForList): boolean {
        return false;
    }

    /*private isDueDateSoon(item: IChecklistScheduleForList): boolean {
        return item.IsActive && this.fiChecklistHelperService.isTimeLessThenDay(item.DueDateTime);
    }*/

    protected isScheduleTypeRed(item: IChecklistScheduleForList): boolean {
        if (item.OperationalPeriodStatus === PublicationItemStatus.NotShared) {
            return false;
        }

        return (item.IsActive && this.isOnDemandSchedule(item.ScheduleType)) || this.isAuditSchedule(item.ScheduleType);
    }
}
